import React, { InputHTMLAttributes, ReactElement, useMemo } from 'react';
import { IconProps } from '../icons/generated';
import {
  getSegmentedControlOptionStyleByProps,
  getSegmentedControlOptionIconStyleBySize,
  getIconSizeBySize,
  getSegmentedControlOptionLabelStyleBySize,
  segmented_option_text_style,
} from './styles';
import type { SegmentedControlSize } from './types';
import styled from '@emotion/styled';
import { hidden } from '../../styles';
import { useFocusRing } from '@react-aria/focus';
import { DisabledTooltip, Tooltip, TooltipProps } from '../tooltip';

export interface SegmentedControlOptionProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'width'> {
  label?: string;
  icon?: ReactElement<IconProps>;
  value?: string;
  checked?: boolean;
  disabled?: boolean;
  /** @default 'medium' */
  size?: SegmentedControlSize;
  /** @default 'auto' */
  width?: 'fill' | 'auto';
  thumbShow?: boolean;
  tooltipProps?: Pick<TooltipProps, 'openerTriggerEvent' | 'placement'>;
  /**
   * IconOnly인 경우 노출됩니다.
   */
  tooltip?: string;
  /**
   * disabled 상태일때 노출됩니다.
   * iconOnly인 경우 {tooltip} ({disabledTooltip}) 형태로 노출됩니다.
   */
  disabledTooltip?: string;
}

export const SegmentedControlOption = React.forwardRef(
  (
    {
      label,
      icon,
      tooltip,
      value,
      checked,
      disabled,
      tooltipProps,
      disabledTooltip,
      size = 'medium',
      width = 'auto',
      thumbShow,
      onChange,
      ...props
    }: SegmentedControlOptionProps,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    if (!label && !icon) {
      throw new Error(' SegmentedControlOption should have at least a label or an icon');
    }

    const { isFocusVisible, focusProps } = useFocusRing({ within: true });

    const { option_style, label_style, icon_style } = useMemo(() => {
      const option_style = getSegmentedControlOptionStyleByProps({
        size,
        checked,
        disabled,
        width,
        focused: isFocusVisible,
        thumbShow,
      });
      const label_style = getSegmentedControlOptionLabelStyleBySize(size);
      const icon_style = getSegmentedControlOptionIconStyleBySize(size);

      return {
        option_style,
        label_style,
        icon_style,
      };
    }, [size, checked, disabled, width, isFocusVisible, thumbShow]);

    const option_element = (
      <label
        className='pds-segmented-control-option'
        css={option_style}
        {...focusProps}
        data-testid='pds-segmented-control-option'
        role='presentation'
      >
        <HiddenInput
          type='radio'
          value={value}
          ref={ref}
          disabled={disabled}
          checked={checked}
          onChange={(evt) => onChange?.(evt)}
          {...props}
        />
        <div
          className='pds-segmented-control-option-label'
          css={label_style}
          aria-label={label || (icon?.type ? 'SegmentedControl Icon' : '')}
          aria-checked={checked}
          aria-disabled={disabled}
        >
          {icon && (
            <span
              className='pds-segmented-control-option-icon'
              css={icon_style}
              data-testid='pds-segmented-control-option-icon'
            >
              {React.cloneElement(icon, {
                size: getIconSizeBySize(size),
                color: 'currentColor',
              })}
            </span>
          )}
          {label && (
            <span css={segmented_option_text_style} data-testid='pds-segmented-control-option-text'>
              {label}
            </span>
          )}
        </div>
      </label>
    );

    return !label && icon && tooltip ? (
      <Tooltip
        content={`${tooltip}${Boolean(disabledTooltip && disabled) ? ` (${disabledTooltip})` : ''}`}
        placement={tooltipProps?.placement ?? 'bottom'}
        kind='neutral'
        openerTriggerEvent={tooltipProps?.openerTriggerEvent ?? 'hover'}
        close_delay={50}
      >
        {option_element}
      </Tooltip>
    ) : (
      <DisabledTooltip
        content={disabledTooltip ?? ''}
        disabled={Boolean(disabledTooltip && disabled)}
        {...tooltipProps}
      >
        {option_element}
      </DisabledTooltip>
    );
  },
);

const HiddenInput = styled.input`
  ${hidden};
  &:focus {
    outline: none;
    box-shadow: none;
  }
`;
